import { TravelBillingPreview } from "@/components/TravelBillingPreview";
// "Import timesheet hours" dialog — pulls approved fieldworker hours on a job
// into invoice line items (labour + machine hours, one line per worker/machine
// per day). Labour and machine lock separately so nothing gets double-billed
// or left unclaimed.
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cutoffDate } from "@/lib/billing";

const fmtDate = (d) => {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" });
  } catch {
    return d;
  }
};

export function TimesheetHoursImport({ open, onOpenChange, jobId, existingIds, onAdd, cutoffDay }) {
  const [entries, setEntries] = useState([]);
  const [labourPreview, setLabourPreview] = useState([]);
  const [rate, setRate] = useState("");
  const [machineRate, setMachineRate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const load = async (f = from, t = to) => {
    if (!jobId) return;
    setLoading(true);
    try {
      const params = {};
      if (f) params.from_date = f;
      if (t) params.to_date = t;
      const { data } = await api.get(`/jobs/${jobId}/billable-hours`, { params });
      const rows = (data.entries || []).filter((e) => e.kind !== "material").map((e) => ({ ...e, lock_key: e.lock_key || `${e.timesheet_id}|l` }));
      setEntries(rows);
      setLabourPreview((data.labour_preview || []).filter(row => !existingIds.has(`${row.timesheet_id}|l`)));
      setRate((r) => (r === "" && data.default_rate ? String(data.default_rate) : r));
      setMachineRate((r) => (r === "" && data.default_machine_rate ? String(data.default_machine_rate) : r));
      // Pre-select every available (not-yet-invoiced) entry
      setSelected(new Set(rows
        .filter((e) => !e.invoiced_invoice_id && !existingIds.has(e.lock_key))
        .map((e) => e.lock_key)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load timesheet hours");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      const cut = cutoffDate(cutoffDay) || "";
      setFrom(""); setTo(cut); setRate(""); setMachineRate("");
      load("", cut);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const available = entries.filter((e) => !e.invoiced_invoice_id && !existingIds.has(e.lock_key));
  const allSelected = available.length > 0 && available.every((e) => selected.has(e.lock_key));
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(available.map((e) => e.lock_key)));
  };

  const picked = useMemo(() => entries.filter((e) => selected.has(e.lock_key)), [entries, selected]);
  const labourHours = picked.filter((e) => e.kind !== "machine").reduce((s, e) => s + (e.hours || 0), 0);
  const machineHours = picked.filter((e) => e.kind === "machine").reduce((s, e) => s + (e.hours || 0), 0);
  const rateNum = parseFloat(rate) || 0;
  const machineRateNum = parseFloat(machineRate) || 0;
  // Per-entry charge-out rates (worker / machine) win over the job defaults
  const effRate = (e) => (e.rate != null && e.rate !== "" ? Number(e.rate) : (e.kind === "machine" ? machineRateNum : rateNum));
  const totalValue = picked.reduce((s, e) => s + (e.hours || 0) * effRate(e), 0);

  const addSelected = () => {
    if (!picked.length) return toast.error("Select at least one entry");
    const lines = picked.map((e) => (e.kind === "machine"
      ? {
          description: `Machine — ${e.asset_name || "Plant"} (${fmtDate(e.date)})${e.notes ? ` — ${e.notes}` : ""}`,
          qty: e.hours,
          unit: "hr",
          unit_price: effRate(e),
          discount_pct: 0,
          source_timesheet_id: e.timesheet_id,
          source_kind: "machine",
        }
      : {
          description: `Labour — ${e.user_name} (${fmtDate(e.date)})${e.notes ? ` — ${e.notes}` : ""}`,
          qty: e.hours,
          unit: "hr",
          unit_price: effRate(e),
          discount_pct: 0,
          source_timesheet_id: e.timesheet_id,
          source_kind: "labour",
        }));
    onAdd(lines, rateNum);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="timesheet-hours-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#FF5F15]" /> Import timesheet hours
          </DialogTitle>
          <DialogDescription className="text-xs">
            Approved hours on this job — labour and machine time, one line per day. Workers/machines with a charge-out rate price automatically (shown in orange); others use the fallback $/hr. Already-invoiced entries are locked so you can't double-bill.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="label-tag">From</Label>
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); load(e.target.value, to); }}
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="ts-import-from" />
          </div>
          <div>
            <Label className="label-tag">To</Label>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); load(from, e.target.value); }}
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="ts-import-to" />
          </div>
          <div>
            <Label className="label-tag">Labour $/hr <span className="text-zinc-400 normal-case">(fallback)</span></Label>
            <Input type="number" step="0.5" min="0" value={rate} onChange={(e) => setRate(e.target.value)}
              placeholder="e.g. 95" className="mt-1.5 rounded-sm border-zinc-400" data-testid="ts-import-rate" />
          </div>
          <div>
            <Label className="label-tag">Machine $/hr <span className="text-zinc-400 normal-case">(fallback)</span></Label>
            <Input type="number" step="0.5" min="0" value={machineRate} onChange={(e) => setMachineRate(e.target.value)}
              placeholder="e.g. 150" className="mt-1.5 rounded-sm border-zinc-400" data-testid="ts-import-machine-rate" />
          </div>
        </div>

        {!loading && <TravelBillingPreview rows={labourPreview} />}
        {loading ? (
          <div className="space-y-2 py-2">{[1, 2, 3].map((i) => (<div key={i} className="h-10 bg-zinc-100 animate-pulse border border-zinc-200" />))}</div>
        ) : entries.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500" data-testid="ts-import-empty">
            No approved work hours on this job{from || to ? " in that date range" : ""}.
          </div>
        ) : (
          <div className="border border-zinc-300 overflow-x-auto max-h-72 overflow-y-auto" data-testid="ts-import-list">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 border-b border-zinc-300 sticky top-0">
                <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                  <th className="px-2 py-2">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-[#FF5F15]" data-testid="ts-import-select-all" />
                  </th>
                  <th className="px-2 py-2">Worker / Machine</th>
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2 text-right">Hours</th>
                  <th className="px-2 py-2 text-right">$/hr</th>
                  <th className="px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {entries.map((e) => {
                  const onInvoice = existingIds.has(e.lock_key);
                  const locked = !!e.invoiced_invoice_id || onInvoice;
                  return (
                    <tr key={e.lock_key + (e.asset_name || "")} className={locked ? "bg-zinc-50 text-zinc-400" : "hover:bg-orange-50"} data-testid={`ts-import-row-${e.lock_key}`}>
                      <td className="px-2 py-1.5">
                        <input type="checkbox" disabled={locked} checked={selected.has(e.lock_key)}
                          onChange={() => toggle(e.lock_key)} className="accent-[#FF5F15]" data-testid={`ts-import-check-${e.lock_key}`} />
                      </td>
                      <td className="px-2 py-1.5 font-bold">
                        {e.kind === "machine" ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Truck className="w-3.5 h-3.5 text-[#FF5F15]" /> {e.asset_name}
                            <span className="text-[10px] font-normal text-zinc-400">({e.user_name})</span>
                          </span>
                        ) : e.user_name}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-xs">{fmtDate(e.date)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{(e.hours || 0).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs" data-testid={`ts-import-rate-${e.lock_key}`}>
                        {effRate(e) > 0 ? (
                          <span className={e.rate != null && e.rate !== "" ? "font-bold text-[#FF5F15]" : ""} title={e.rate != null && e.rate !== "" ? "Charge-out rate set on this worker/machine" : "Job default rate"}>
                            ${effRate(e).toFixed(2)}
                          </span>
                        ) : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        {onInvoice ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700">On this invoice</span>
                        ) : e.invoiced_invoice_id ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Invoiced {e.invoice_number || ""}</span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Billable</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter className="items-center gap-3 sm:justify-between">
          <div className="font-mono text-sm" data-testid="ts-import-summary">
            {picked.length} entr{picked.length === 1 ? "y" : "ies"} · {labourHours.toFixed(2)} lab hrs
            {machineHours > 0 && <span> · {machineHours.toFixed(2)} mach hrs</span>}
            {totalValue > 0 && <span className="font-bold text-[#FF5F15]"> · ${totalValue.toFixed(2)}</span>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm uppercase tracking-wider font-bold text-xs">
              Cancel
            </Button>
            <Button onClick={addSelected} disabled={!picked.length}
              className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
              data-testid="ts-import-add">
              Add {picked.length || ""} line{picked.length === 1 ? "" : "s"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
